import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import { userKeys } from '../../core/query-keys';
import { userEndpoints, adminEndpoints, invitationEndpoints } from './users.endpoints';
import type {
  CreateRoleRequest,
  LocalUserProfile,
  PermissionLevel,
  PermissionsMatrix,
  RoleInfo,
  User,
  UserListResponse,
  UpdateUserRequest,
  InviteUserRequest,
  AdminUpdateUserRequest,
  AdminPasswordReset,
  InvitationInfo,
  InvitationListResponse,
  RoleRecord,
  CompleteRegistrationRequest,
  PasswordRecoverRequest,
  PasswordRecoverConfirmRequest,
  UserSearchResult,
} from './users.types';

/** All invitation-list cache entries (prefix match), invalidated on any invite mutation. */
const invitationsKey = ['invitations'] as const;

export function useCurrentUser() {
  return useQuery({
    queryKey: userKeys.me,
    queryFn: () => api.get<LocalUserProfile>(userEndpoints.me),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UpdateUserRequest) => api.put<void>(userEndpoints.me, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

/** One page of the directory — the API takes only `limit` (server caps at 100)
 *  and `offset`, and returns `total`. */
export const USER_PAGE = 20;

/** The directory, a page at a time. It used to fetch ONE page of 50 and stop, so
 *  user 51 onward silently never appeared (2026-09-29). `hasNextPage` stays true
 *  until `total` is loaded (or a page comes back empty), and the screen offers
 *  "Load more" only then. Mutations still invalidate `userKeys.all`, which
 *  refetches every page loaded so far. */
export function useAdminListUsers() {
  return useInfiniteQuery({
    queryKey: userKeys.list(USER_PAGE, 0),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.get<UserListResponse>(`${adminEndpoints.users}?limit=${USER_PAGE}&offset=${pageParam}`),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.users.length, 0);
      return last.users.length > 0 && loaded < last.total ? loaded : undefined;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** R-113: audit "User" filter typeahead — resolve a name/email fragment → up to
 *  10 matches (server-side search; never loads the whole directory). */
export function useAdminSearchUsers(q: string) {
  return useQuery({
    queryKey: ['admin', 'user-search', q],
    queryFn: () => api.get<UserSearchResult[]>(adminEndpoints.userSearch(q)),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });
}

/** R-113: fetch ONE user on demand (audit details modal) — no directory preload. */
export function useAdminGetUser(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => api.get<User>(adminEndpoints.user(id as string)),
    enabled: !!id,
    staleTime: 60_000,
  });
}

/** Pending + expired invitations, a page at a time (same shape and cap as the
 *  user list). It used to read ONE page of 50 and stop (2026-09-29). */
export function useAdminListInvitations() {
  return useInfiniteQuery({
    queryKey: userKeys.invitations(USER_PAGE, 0),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.get<InvitationListResponse>(`${adminEndpoints.invitations}?limit=${USER_PAGE}&offset=${pageParam}`),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.invitations.length, 0);
      return last.invitations.length > 0 && loaded < last.total ? loaded : undefined;
    },
  });
}

export function useAdminListRoles() {
  return useQuery({
    queryKey: userKeys.roles,
    queryFn: () => api.get<RoleRecord[]>(adminEndpoints.roles),
    staleTime: 5 * 60 * 1000, // role registry rarely changes
  });
}

export function useAdminInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: InviteUserRequest) => api.post<void>(adminEndpoints.invite, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationsKey }),
  });
}

export function useAdminRefreshInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.post<void>(adminEndpoints.refreshInvitation(email)),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationsKey }),
  });
}

export function useAdminRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.delete<void>(adminEndpoints.revokeInvitation(email)),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationsKey }),
  });
}

export function useAdminUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, req }: { id: string; req: AdminUpdateUserRequest }) =>
      api.put<void>(adminEndpoints.user(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

/** Assign (or clear) the one site a user may reach. Clearing REMOVES their site access. */
export function useSetHomeSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, siteId }: { id: string; siteId: string | null }) =>
      api.put<void>(adminEndpoints.homeSite(id), { site_id: siteId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useAdminDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(adminEndpoints.user(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

/** R-087 — enable/disable a user. Disabling revokes sessions + blocks login. */
export function useAdminSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post<void>(adminEndpoints.setActive(id), { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

/** Admin 2FA reset (D-080): drops the user's authenticator; they re-enrol at next sign-in. */
export function useAdminResetMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<boolean>(adminEndpoints.resetMfa(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

/** Admin-initiated password reset: returns {email, otp} so the UI can show a shareable reset link. */
export function useAdminCreatePasswordReset() {
  return useMutation({
    mutationFn: (id: string) => api.post<AdminPasswordReset>(adminEndpoints.resetLink(id)),
  });
}

/** Request a password-reset OTP be emailed to `email`. No auth — the user is locked out. */
export function usePasswordRecover() {
  return useMutation({
    mutationFn: (req: PasswordRecoverRequest) =>
      api.post<void>(userEndpoints.passwordRecover, req, { noAuth: true }),
  });
}

/** Confirm the emailed OTP and set a new password. No auth. */
export function usePasswordRecoverConfirm() {
  return useMutation({
    mutationFn: (req: PasswordRecoverConfirmRequest) =>
      api.post<void>(userEndpoints.passwordRecoverConfirm, req, { noAuth: true }),
  });
}

export function useGetInvitation(token: string) {
  return useQuery({
    queryKey: userKeys.invite(token),
    queryFn: () => api.get<InvitationInfo>(invitationEndpoints.get(token), { noAuth: true }),
    enabled: token.length > 0,
    retry: false,
  });
}

export function useCompleteRegistration(token: string) {
  return useMutation({
    mutationFn: (req: CompleteRegistrationRequest) =>
      api.post<void>(invitationEndpoints.complete(token), req, { noAuth: true }),
  });
}

// ── Access & Roles console (R-058 phase B) ──────────────────────────────────

const permissionsKey = ['admin', 'permissions'] as const;

export function useAdminPermissionsMatrix() {
  return useQuery({
    queryKey: permissionsKey,
    queryFn: () => api.get<PermissionsMatrix>(adminEndpoints.permissions),
  });
}

/** Set a matrix cell (module × role → level). Edits reach sites ≤60s (sync sweep). */
export function useSetPermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, module, level }: { roleId: string; module: string; level: PermissionLevel }) =>
      api.put<void>(adminEndpoints.permissionCell(roleId, module), { level }),
    onSuccess: () => qc.invalidateQueries({ queryKey: permissionsKey }),
  });
}

/** Clear a matrix cell back to no-access. */
export function useClearPermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, module }: { roleId: string; module: string }) =>
      api.delete<boolean>(adminEndpoints.permissionCell(roleId, module)),
    onSuccess: () => qc.invalidateQueries({ queryKey: permissionsKey }),
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateRoleRequest) => api.post<RoleInfo>(adminEndpoints.roles, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: permissionsKey });
      qc.invalidateQueries({ queryKey: userKeys.roles });
    },
  });
}
